import { lazy, type ComponentType } from "react";

type AnyProps = Record<string, unknown>;
type Loader = () => Promise<{ default: ComponentType<AnyProps> }>;

export type LazyRoute = ComponentType<AnyProps> & { preload: () => Promise<void> };

const RELOAD_KEY = "d9l:chunk-reload";

/**
 * A code-split page that can be loaded BEFORE React renders it.
 *
 * Every page used to ship in one 1.7 MB entry chunk, so a visitor to "/" also
 * downloaded the admin panel, the dashboard and recharts. Splitting per route
 * fixes that, but a plain React.lazy() suspends on first render, and because
 * main.tsx replaces the prerendered HTML with createRoot(), that would blank a
 * page the visitor can already see. So:
 *
 * - main.tsx calls preload() for the current URL before the first render. Once
 *   the module is in hand this component renders it directly and never
 *   suspends: the prerendered page is swapped for an identical live one.
 * - Internal links call preload() on hover/touch (see installRoutePrefetch), so
 *   by the time the click lands the next page is usually already here.
 */
export function lazyRoute(loader: Loader): LazyRoute {
  let Loaded: ComponentType<AnyProps> | undefined;
  let pending: Promise<void> | undefined;

  const preload = () =>
    (pending ??= loader().then(
      (m) => {
        Loaded = m.default;
      },
      (error) => {
        pending = undefined;
        throw error;
      },
    ));

  const Lazy = lazy(() =>
    preload().then(
      () => ({ default: Loaded! }),
      (error) => {
        // A chunk from a previous deploy is gone. The router has already moved
        // to the new URL, so a real page load of it gets the current build.
        // Once per session only, so a genuinely broken chunk cannot loop.
        try {
          if (!sessionStorage.getItem(RELOAD_KEY)) {
            sessionStorage.setItem(RELOAD_KEY, "1");
            window.location.reload();
            return new Promise<never>(() => {});
          }
        } catch {
          // storage blocked: fall through and surface the error
        }
        throw error;
      },
    ),
  );

  const Route = ((props: AnyProps) => (Loaded ? <Loaded {...props} /> : <Lazy {...props} />)) as LazyRoute;
  Route.preload = preload;
  return Route;
}
